import React, { type ComponentProps, type ReactNode, useMemo } from 'react'

import { LinkProvider } from '../../components/LinkProvider'
import { LayersProvider } from './Layer'
import {
  InjectStylesIntoThemeProviderContext,
  ThemeProvider,
} from './ThemeProvider'
import { ToastProvider } from './Toast'

type CommonProps = {
  /**
   * A React component that renders a link. This component should implement whatever logic
   * is necessary to handle routing in your application.
   *
   * See also the `LinkProvider` docs.
   */
  linkComponent: ComponentProps<typeof LinkProvider>['linkComponent']
  /**
   * The number of milliseconds that a toast rendered inside of this component will
   * persist by default. You can pass a different `duration` to `addToast` to override
   * this value.
   *
   * See also the `ToastProvider` docs.
   */
  defaultToastDuration: ComponentProps<typeof ToastProvider>['defaultDuration']
}

type Props = CommonProps & {
  /**
   * A standard Robinhood theme object, as defined here in Bento Web.
   */
  theme: ComponentProps<typeof ThemeProvider>['theme']
  children: ComponentProps<typeof ThemeProvider>['children']
}

type ThemelessProps = CommonProps & {
  children: ReactNode
  /**
   * Custom styles that will be injected inside every instance of `ThemeProvider`.
   *
   * Do not use this anywhere except the one place in Web App that already uses it.
   */
  __dangerouslyInjectStylesIntoThemeProvider?: { [cssProperty: string]: string }
}

/**
 * Include this at the top of your app's React tree to use Bento Web product components. This
 * component contains all React context providers that your app must have in order to be able
 * to use any Bento Web product components.
 *
 * This should not be used in apps that want to use only Bento Web brand components (i.e. the
 * static sites).
 *
 * See `ThemelessBentoProvider` if you need to do intensive custom theming in your app, but
 * you probably don't want to use it.
 */
export function BentoProvider({ children, theme, ...themelessProps }: Props) {
  return (
    <ThemeProvider theme={theme}>
      {(...themeProviderArgs) => (
        <ThemelessBentoProvider {...themelessProps}>
          {children(...themeProviderArgs)}
        </ThemelessBentoProvider>
      )}
    </ThemeProvider>
  )
}

/**
 *
 * If you need to do intensive custom theming in your app, you can use `ThemelessBentoProvider`.
 * It is a variant of `BentoProvider` that _does not_ include the Bento Web `ThemeProvider`.
 * If you use this component, you therefore must include a `ThemeProvider` somewhere above it
 * in the React component tree.
 *
 * You probably don't want to use this component.
 */
export function ThemelessBentoProvider({
  __dangerouslyInjectStylesIntoThemeProvider: stylesToInjectProp,
  children,
  defaultToastDuration,
  linkComponent,
}: ThemelessProps) {
  const stylesToInject = useMemo(() => {
    return stylesToInjectProp || {}
  }, [stylesToInjectProp])

  return (
    <LayersProvider>
      <LinkProvider linkComponent={linkComponent}>
        <ToastProvider defaultDuration={defaultToastDuration}>
          <InjectStylesIntoThemeProviderContext.Provider value={stylesToInject}>
            {children}
          </InjectStylesIntoThemeProviderContext.Provider>
        </ToastProvider>
      </LinkProvider>
    </LayersProvider>
  )
}
